import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import api from '../../utils/axiosConfig'; // Use configured API instance
import AuthContext from './AuthContext'; // Make sure this path is correct
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appleIdInitialized, setAppleIdInitialized] = useState(false);

  // Use the AuthContext for login/registration
  const { login, register: registerUser, lineLogin, appleLogin, googleLogin } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();

  // Google Identity Services
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const googleBtnRef = useRef(null);

  // Handle the Google ID-token credential returned by GIS.
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await googleLogin(credential);
      if (result.success) {
        navigate(result.returnUrl || '/dashboard', { replace: true });
      } else {
        setError(result.error || 'Google login failed');
      }
    } catch (err) {
      console.error('[LOGIN] Google login error:', err);
      setError('Google login failed');
    } finally {
      setLoading(false);
    }
  };

  // Initialize & render the Google button (web). In a native Capacitor build,
  // the embedded WebView cannot run Google's web OAuth flow — use a native
  // Google Auth plugin there (see GOOGLE_LOGIN_SETUP.md).
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!GOOGLE_CLIENT_ID) {
      console.warn('[LOGIN] REACT_APP_GOOGLE_CLIENT_ID not set — Google login hidden');
      return;
    }

    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryInit, 150);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'continue_with',
        });
      }
    };
    tryInit();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);

  // Clean up any stale or unwanted returnUrl on mount
  // We only allow specific URLs to persist as post-login redirects
  useEffect(() => {
    const ru = localStorage.getItem('returnUrl');
    const allowedReturnUrls = ['/tour-guide', '/review_input', '/coupon_input'];
    if (ru && !allowedReturnUrls.includes(ru)) {
      localStorage.removeItem('returnUrl');
    }
  }, []);

  // Initialize Apple Sign In once when component mounts (web only)
  useEffect(() => {
    // Only initialize for web (not native iOS)
    if (Capacitor.isNativePlatform()) {
      console.log('[LOGIN] Native platform detected, skipping web Apple Sign In init');
      return;
    }

    // Wait for Apple SDK to be fully loaded
    const initializeAppleSignIn = () => {
      if (!window.AppleID) {
        console.log('[LOGIN] Apple SDK not loaded yet, retrying...');
        return false;
      }

      try {
        // Get the correct redirect URI based on environment
        const redirectURI = window.location.hostname === 'localhost'
          ? 'http://localhost:3000/login'
          : 'https://travy.buzz/login';

        console.log('[LOGIN] Initializing Apple Sign In with config:', {
          clientId: 'app.travy.login',
          redirectURI: redirectURI,
          hostname: window.location.hostname,
          scope: 'email name',
          usePopup: true
        });

        // Initialize Apple ID once
        window.AppleID.auth.init({
          clientId: 'app.travy.login',
          scope: 'email name',
          redirectURI: redirectURI,
          state: 'web_apple_login',
          usePopup: true
        });

        // Add event listeners for Apple Sign In events
        document.addEventListener('AppleIDSignInOnSuccess', (event) => {
          console.log('✅ [LOGIN] AppleIDSignInOnSuccess event fired:', event.detail);
        });

        document.addEventListener('AppleIDSignInOnFailure', (event) => {
          console.error('❌ [LOGIN] AppleIDSignInOnFailure event fired:', event.detail);
        });

        setAppleIdInitialized(true);
        console.log('✅ [LOGIN] Apple Sign In initialized successfully');
        return true;
      } catch (err) {
        console.error('❌ [LOGIN] Failed to initialize Apple Sign In:', err);
        return false;
      }
    };

    // Try to initialize immediately
    if (!initializeAppleSignIn()) {
      // If failed, retry after SDK loads
      const checkInterval = setInterval(() => {
        if (initializeAppleSignIn()) {
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 5 seconds
      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        console.error('❌ [LOGIN] Apple SDK failed to load within timeout');
      }, 5000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
      };
    }
    // Run once on mount — re-running on appleIdInitialized would re-init the
    // Apple SDK and stack duplicate document event listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API_URL now handled by the api instance configuration
  // Configure api instance (already configured in axiosConfig.js)

  // LINE Channel ID - add this to your .env file as REACT_APP_LINE_CHANNEL_ID
  const LINE_CHANNEL_ID = process.env.REACT_APP_LINE_CHANNEL_ID;
  
  // Your app's redirect URI (add this to your .env file)
  const LINE_REDIRECT_URI = process.env.REACT_APP_LINE_REDIRECT_URI || `${window.location.origin}/login`;

  // Handle LINE login callback
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      setError('การเข้าสู่ระบบด้วย LINE ถูกยกเลิก');
      // Clean up URL
      navigate('/login', { replace: true });
      return;
    }

    if (code && state === 'line_login') {
      handleLineCallback(code);
    }
  }, [location]);

  // REMOVED: handleSuccessfulAuth - AuthContext now handles returnUrl

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegistering) {
        // Registration
        if (!name) {
          setError('ชื่อไม่สามารถว่างเปล่าได้');
          setLoading(false);
          return;
        }

        const result = await registerUser(email, password, name);

        if (result.success) {
          // Registration returns { success: true, returnUrl: '/dashboard' }
          const redirectTo = result.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Registration successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          setError(result.error || 'Registration failed');
        }
      } else {
        // Login
        const result = await login(email, password);

        if (result.success) {
          // Login returns { success: true, returnUrl: '/dashboard' }
          const redirectTo = result.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Login successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          setError(result.error || 'Login failed');
        }
      }
    } catch (err) {
      console.error('[LOGIN] Error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // LINE login handler - redirect to LINE authorization
  const handleLineLogin = () => {
    if (!LINE_CHANNEL_ID) {
      setError('LINE Channel ID ไม่ได้ถูกกำหนดค่า');
      return;
    }

    // Generate a random state for security
    const state = 'line_login';
    
    // Build LINE authorization URL
    const lineAuthUrl = new URL('https://access.line.me/oauth2/v2.1/authorize');
    lineAuthUrl.searchParams.append('response_type', 'code');
    lineAuthUrl.searchParams.append('client_id', LINE_CHANNEL_ID);
    lineAuthUrl.searchParams.append('redirect_uri', LINE_REDIRECT_URI);
    lineAuthUrl.searchParams.append('state', state);
    lineAuthUrl.searchParams.append('scope', 'profile openid email');
    
    // Redirect to LINE
    window.location.href = lineAuthUrl.toString();
  };

  // Handle LINE callback with authorization code
  const handleLineCallback = async (code) => {
    setLoading(true);
    setError('');

    try {
      const result = await lineLogin(code, LINE_REDIRECT_URI);

      if (result.success) {
        const redirectTo = result.returnUrl || '/dashboard';
        console.log('✅ [LOGIN] LINE login successful, redirecting to:', redirectTo);
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.error || 'LINE login failed');
        // Clean up URL
        navigate('/login', { replace: true });
      }
    } catch (err) {
      console.error('[LOGIN] LINE login error:', err);
      setError('LINE login failed');
      // Clean up URL
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  // Apple Sign In handler - works for both native iOS and web
  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if running on native iOS
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        // Native iOS flow using Capacitor plugin
        const result = await SignInWithApple.authorize({
          clientId: 'app.travy.mobile', // Your app's bundle ID
          redirectURI: 'https://travy.buzz/login', // Your redirect URI
          scopes: 'email name',
          state: 'apple_login'
        });

        console.log('[LOGIN] Native Apple Sign In result:', result);

        // Send the authorization data to backend
        const loginResult = await appleLogin({
          identityToken: result.response.identityToken,
          authorizationCode: result.response.authorizationCode,
          user: result.response.user,
          email: result.response.email,
          givenName: result.response.givenName,
          familyName: result.response.familyName
        });

        if (loginResult.success) {
          const redirectTo = loginResult.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Apple login successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          setError(loginResult.error || 'Apple login failed');
        }
      } else {
        // Web flow using Apple JS SDK
        if (!window.AppleID) {
          console.error('❌ [LOGIN] Apple SDK not available');
          setError('Apple Sign In is not available. Please refresh the page.');
          setLoading(false);
          return;
        }

        // Check if AppleID is initialized
        if (!appleIdInitialized) {
          console.error('❌ [LOGIN] Apple Sign In not initialized yet');
          setError('Apple Sign In is still loading. Please try again in a moment.');
          setLoading(false);
          return;
        }

        console.log('🍎 [LOGIN] Triggering Apple Sign In popup...');

        try {
          // Trigger sign in (already initialized in useEffect)
          const data = await window.AppleID.auth.signIn();
          console.log('✅ [LOGIN] Apple Sign In popup completed successfully');
          console.log('[LOGIN] Apple Sign In data received:', {
            hasAuthorization: !!data?.authorization,
            hasIdToken: !!data?.authorization?.id_token,
            hasCode: !!data?.authorization?.code,
            hasUser: !!data?.user,
            hasEmail: !!data?.user?.email,
            hasName: !!data?.user?.name
          });

          if (!data?.authorization?.id_token) {
            throw new Error('No identity token received from Apple');
          }

          // Send authorization data to backend
          const applePayload = {
            identityToken: data.authorization.id_token,
            authorizationCode: data.authorization.code,
            user: data.user?.name ? {
              firstName: data.user.name.firstName,
              lastName: data.user.name.lastName
            } : null,
            email: data.user?.email,
            givenName: data.user?.name?.firstName,
            familyName: data.user?.name?.lastName
          };

          console.log('[LOGIN] Sending Apple login data to backend...');
          const loginResult = await appleLogin(applePayload);

          if (loginResult.success) {
            const redirectTo = loginResult.returnUrl || '/dashboard';
            console.log('✅ [LOGIN] Apple login successful, redirecting to:', redirectTo);
            navigate(redirectTo, { replace: true });
          } else {
            console.error('❌ [LOGIN] Backend rejected Apple login:', loginResult.error);
            setError(loginResult.error || 'Apple login failed');
          }
        } catch (signInError) {
          // Handle specific Apple Sign In errors
          console.error('❌ [LOGIN] Apple Sign In popup error:', signInError);
          throw signInError;
        }
      }
    } catch (err) {
      console.error('❌ [LOGIN] Apple Sign In error:', {
        error: err.error,
        message: err.message,
        code: err.code,
        fullError: err
      });

      // Handle user cancellation gracefully
      if (err.error === 'popup_closed_by_user' || err.message?.includes('popup_closed_by_user')) {
        console.log('ℹ️ [LOGIN] User cancelled Apple Sign In - not showing error');
        // Don't show error for cancellation
      } else if (err.message?.includes('1001')) {
        console.log('ℹ️ [LOGIN] User cancelled Apple Sign In (error 1001) - not showing error');
        // Don't show error for cancellation
      } else {
        // Show error for actual failures
        const errorMessage = err.message || 'การเข้าสู่ระบบด้วย Apple ล้มเหลว';
        console.error('❌ [LOGIN] Showing error to user:', errorMessage);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-container">
          <h1>Login</h1>
        </div>
        
        <h2>{isRegistering ? 'สมัครสมาชิก' : 'เข้าสู่ระบบ'}</h2>
        
        {error && <div className="error-message">{error}</div>}

        {/* No special return-to-tour UI; post-login routing is now centralized */}

        <form onSubmit={handleSubmit}>
          {isRegistering && (
            <div className="form-group">
              <label htmlFor="name">ชื่อผู้ใช้งาน</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="กรุณากรอกชื่อผู้ใช้งาน"
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">อีเมล</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">รหัสผ่าน</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'กำลังดำเนินการ...' : isRegistering ? 'สมัครสมาชิก' : 'เข้าสู่ระบบ'}
          </button>
        </form>

        <div className="social-login">
          <div className="separator">
            <span>หรือ</span>
          </div>

          {/* Google Sign-In (rendered by Google Identity Services on web) */}
          <div
            ref={googleBtnRef}
            className="google-button-container"
            style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}
          />

          <button
            onClick={handleLineLogin}
            className="line-button"
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.64 6.8c0 .96-.32 1.84-.96 2.56-.32.32-.64.64-1.12.8-.16.08-.32.16-.48.16-.16 0-.32-.08-.48-.16l-1.6-.8-1.6.8c-.16.08-.32.16-.48.16s-.32-.08-.48-.16c-.48-.16-.8-.48-1.12-.8-.64-.72-.96-1.6-.96-2.56 0-1.92 1.76-3.52 3.84-3.52s3.84 1.6 3.84 3.52z" fill="white"/>
            </svg>
            {isRegistering ? 'ลงทะเบียนด้วย LINE' : 'เข้าสู่ระบบด้วย LINE'}
          </button>

          <button
            onClick={handleAppleSignIn}
            className="apple-button"
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="white"/>
            </svg>
            {isRegistering ? 'Sign up with Apple' : 'Sign in with Apple'}
          </button>
        </div>
        
        <div className="switch-mode">
          {isRegistering ? (
            <p>มีบัญชีอยู่แล้ว? <button onClick={() => setIsRegistering(false)}>เข้าสู่ระบบ</button></p>
          ) : (
            <p>ยังไม่มีบัญชี? <button onClick={() => setIsRegistering(true)}>สมัครสมาชิก</button></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;